import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Input } from '../ui/input';
import { SimpleTable } from '../k2/K2Tables';
import { fmtKc } from '../k2/k2utils';
import { rankPartners } from './analyticsUtils';

const Pct = ({ v }) => (v == null ? <span className="text-[#94A3B8]">—</span> : (
  <span className={v >= 0 ? 'text-[#16A34A]' : 'text-[#DC2626]'}>{v >= 0 ? '+' : ''}{v.toFixed(1)} %</span>
));

export const Panel = ({ title, subtitle, right, testId, children }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5 shadow-sm space-y-4" data-testid={testId}>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h2 className="text-base md:text-lg font-bold text-[#052E16] font-['Manrope']">{title}</h2>
        {subtitle && <p className="text-xs text-[#57534E]">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);

export const SearchBox = ({ value, onChange, testId, placeholder = 'Hledat…' }) => (
  <div className="relative w-64">
    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
    <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="pl-9" data-testid={testId} />
  </div>
);

export const TopBars = ({ data, valueKey, fmt, color, testId }) => (
  <div className="h-[340px]" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
        <XAxis type="number" tick={{ fontSize: 11, fill: '#57534E' }} />
        <YAxis type="category" dataKey="label" width={280} tick={{ fontSize: 11, fill: '#1C1917' }} />
        <Tooltip formatter={(v, n, p) => fmt(v, p.payload)} />
        <Bar dataKey={valueKey} fill={color} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  </div>
);

const short = (s) => (s && s.length > 40 ? `${s.slice(0, 40)}…` : s);

export const PartnerRanking = ({ docs, range, withPrev, label, active, onSelect }) => {
  const [q, setQ] = useState('');
  const all = useMemo(() => rankPartners(docs, range, withPrev), [docs, range, withPrev]);
  const rows = all.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const cols = [
    { key: 'rank', label: '#', className: 'text-[#94A3B8] tabular-nums' },
    { key: 'name', label: label, className: 'font-medium' },
    { key: 'count', label: 'Dokladů', num: true },
    { key: 'amount', label: 'Hodnota', num: true, render: (r) => fmtKc(r.amount), className: 'font-semibold' },
    { key: 'share', label: 'Podíl', num: true, render: (r) => `${r.share.toFixed(1)} %` },
  ];
  if (withPrev) {
    cols.push({ key: 'prev', label: 'Předchozí', num: true, render: (r) => fmtKc(r.prev), className: 'text-[#57534E]' });
    cols.push({ key: 'delta', label: 'Změna', num: true, render: (r) => <Pct v={r.delta} /> });
  }
  return (
    <Panel title={`Žebříček – ${label.toLowerCase()}`} subtitle={`${all.length} subjektů · kliknutím vyfiltrujete doklady`} testId="an-partners"
      right={<SearchBox value={q} onChange={setQ} testId="an-partners-search" />}>
      {active && (
        <button type="button" onClick={() => onSelect(null)} data-testid="an-partner-clear"
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#F0FDF4] text-[#166534] text-xs font-semibold">
          Filtr: {active} <X className="w-3.5 h-3.5" />
        </button>
      )}
      {!q && all.length > 0 && (
        <TopBars testId="an-partners-chart" data={all.slice(0, 10).map((p) => ({ label: short(p.name), amount: p.amount }))} valueKey="amount" fmt={(v) => fmtKc(v)} color="#16A34A" />
      )}
      <SimpleTable key={q} testId="an-partners-table" columns={cols} rows={rows} rowKey={(r) => r.name} onRowClick={(r) => onSelect(r.name === active ? null : r.name)} />
    </Panel>
  );
};
